/**
 * ClientSwitcher — C8 wizard refactor
 *
 * Dropdown topbar pegado al logo (estilo Cortex XDR / Sentinel / Linear).
 * Muestra el client activo; si hay >5 clients, incluye búsqueda inline.
 * Si solo hay 1 client, el selector se oculta (nada que elegir).
 *
 * Se integra en Navigation.js reemplazando el bloque "Tenant Selector" actual.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Buildings as Building2,
  CaretDown as ChevronDown,
  ArrowsClockwise as RefreshCw,
  MagnifyingGlass as Search,
  CheckCircle as Check,
} from '@phosphor-icons/react';
import { useClient } from '@/contexts/ClientContext';
import { useToast } from '@/components/ToastProvider';
import { parseApiError } from '@/lib/utils';

const SEARCH_THRESHOLD = 5;

const ClientSwitcher = ({ collapsed = false }) => {
  const { clients, activeClientId, activeClient, switchClient, bootstrapLoading } =
    useClient();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [switching, setSwitching] = useState(false);
  const dropdownRef = useRef(null);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Si solo hay 1 client o ninguno → no mostrar selector
  if (clients.length <= 1) return null;

  const filtered = clients.filter(
    (c) =>
      !searchTerm ||
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.industry ?? '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = async (clientId) => {
    if (clientId === activeClientId || switching) return;
    setSwitching(true);
    setOpen(false);
    setSearchTerm('');
    try {
      await switchClient(clientId);
    } catch (err) {
      toast.error(parseApiError(err.response?.data?.detail, 'No se pudo cambiar de cliente'));
    } finally {
      setSwitching(false);
    }
  };

  const displayName = activeClient?.name ?? activeClientId ?? 'Select client';

  // Versión colapsada: solo icono
  if (collapsed) {
    return (
      <div className="px-2 py-2 border-b border-white/[0.04]">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-label="Switch client"
          title={displayName}
          className="w-full flex items-center justify-center p-2 rounded-lg bg-v4-panel/[0.03] border border-white/[0.06] hover:border-cyan-500/[0.2] hover:bg-v4-panel/[0.05] transition-all duration-200"
        >
          <Building2 size={14} className="text-cyan-400/70" />
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 py-2.5 border-b border-white/[0.04]" ref={dropdownRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Switch client"
        aria-expanded={open}
        disabled={bootstrapLoading || switching}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-v4-panel/[0.03] border border-white/[0.06] hover:border-cyan-500/[0.2] hover:bg-v4-panel/[0.05] transition-all duration-200 disabled:opacity-50"
      >
        <Building2 size={14} className="text-cyan-400/70 flex-shrink-0" />
        <span className="text-xs font-medium text-slate-300 truncate flex-1 text-left">
          {switching ? 'Switching…' : displayName}
        </span>
        {switching ? (
          <RefreshCw size={12} className="text-slate-600 animate-spin" />
        ) : (
          <ChevronDown
            size={12}
            className={`text-slate-600 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="mt-1.5 bg-v4-panel-2 border border-white/[0.08] rounded-lg shadow-xl shadow-black/40 overflow-hidden">
          {/* Search — solo si hay >SEARCH_THRESHOLD clients */}
          {clients.length > SEARCH_THRESHOLD && (
            <div className="p-2 border-b border-white/[0.06]">
              <div className="relative">
                <Search
                  size={12}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600"
                />
                <input
                  type="text"
                  autoFocus
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar client…"
                  className="w-full pl-7 pr-3 py-1.5 text-xs bg-slate-950/40 border border-white/[0.06] rounded-md text-v4-txt placeholder-slate-600 focus:border-cyan-500/[0.3] focus:outline-none"
                />
              </div>
            </div>
          )}

          {/* Client list */}
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-slate-500 text-center">
                Sin resultados
              </div>
            ) : (
              filtered.map((c) => {
                const isActive = c.id === activeClientId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => handleSelect(c.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-all text-left ${
                      isActive
                        ? 'bg-cyan-500/[0.08] text-cyan-300'
                        : 'text-slate-400 hover:text-v4-txt hover:bg-white/[0.04]'
                    }`}
                  >
                    <Building2
                      size={12}
                      className={isActive ? 'text-cyan-400' : 'text-slate-600'}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      {c.industry && (
                        <div className="text-[10px] text-slate-600 font-mono uppercase tracking-wider truncate">
                          {c.industry}
                        </div>
                      )}
                    </div>
                    {isActive && (
                      <Check size={12} className="text-cyan-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSwitcher;
